import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { Button, ErrorState, Spinner } from 'components/ui'
import { Icon } from 'components/Icon'
import { useAsync } from 'lib/hooks'

import { AuthShell } from './AuthShell'
import { RoleBadge } from './RoleBadge'

const COPY = {
  ar: {
    title: 'اختر مشروعاً',
    subtitle: (count: number) => `لديك صلاحية الوصول إلى ${count} ${count === 1 ? 'مشروع' : 'مشاريع'}. يمكنك التبديل لاحقاً من الشريط العلوي.`,
    continue: 'متابعة',
    signOut: 'تسجيل الخروج',
  },
  en: {
    title: 'Select a project',
    subtitle: (count: number) => `You have access to ${count} project${count === 1 ? '' : 's'}. You can switch later from the top bar.`,
    continue: 'Continue',
    signOut: 'Sign out',
  },
} as const

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

export const SelectProjectPage = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const { data: projects, error, loading, reload } = useAsync(api.fetchProjects, [])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (!user) return <Navigate to="/login" replace />
  if (!loading && !error && (!projects || projects.length <= 1)) {
    return <Navigate to={projects?.[0] ? `/projects/${projects[0].id}` : '/'} replace />
  }

  const selected = selectedId ?? projects?.[0]?.id ?? null

  return (
    <AuthShell>
      {(locale) => {
        const t = COPY[locale]

        if (loading && !projects) return <Spinner />
        if (error) return <ErrorState message={error} onRetry={reload} />
        if (!projects || !selected) return null

        return (
          <>
            <span className="auth__wordmark">JoPath</span>
            <div className="auth__head">
              <h1>{t.title}</h1>
              <p>{t.subtitle(projects.length)}</p>
            </div>
            <ul className="auth__projects" role="radiogroup" aria-label={t.title}>
              {projects.map((project) => {
                const isSelected = project.id === selected
                return (
                  <li key={project.id}>
                    <button
                      type="button"
                      className={isSelected ? 'auth__project is-selected' : 'auth__project'}
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedId(project.id)}
                    >
                      <span className="auth__project-avatar" aria-hidden="true">
                        {initials(project.name)}
                      </span>
                      <span className="auth__project-body">
                        <strong>{project.name}</strong>
                        <span className="auth__project-meta">
                          {project.location ? <small>{project.location}</small> : null}
                          <RoleBadge role={project.myRole} locale={locale} />
                        </span>
                      </span>
                      <span className="auth__project-check" aria-hidden="true">
                        {isSelected ? <Icon name="check" size={16} /> : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
            <Button variant="primary" className="btn--block" onClick={() => navigate(`/projects/${selected}`)}>
              {t.continue}
            </Button>
            <Button
              variant="ghost"
              icon="logout"
              className="btn--block"
              onClick={() => {
                signOut()
                navigate('/login')
              }}
            >
              {t.signOut}
            </Button>
          </>
        )
      }}
    </AuthShell>
  )
}
